import {
  Avatar, Icon, Tag, TagCloseButton, TagLabel, TagProps, useColorModeValue, Tooltip,
} from "@chakra-ui/react";
import { IconType } from "react-icons";
import { UserRole } from "features/User/models";
import { roleStyles } from "../UserTag/helpers";
import { titleize } from "helpers/string";

export interface BaseResourceTargetTagProps extends TagProps {
  remove?: () => void;
  disabled?: boolean;
  name?: string;
  icon?: IconType;
  roleName?: UserRole;
  size?: "xs" | "sm" | "md";
  tagLabelProps?: object;
}

// Visual tokens mirror RoleLabelTag so user, channel, and role chips share one
// pill silhouette across the app.
const SIZE_TOKENS: Record<"xs" | "sm" | "md", {
  tagSize: "sm" | "md";
  fontSize: string;
  avatarBoxSize: string;
  avatarFontSize: string;
  gap: number;
  px: number;
  pl: number;
  pr: number;
}> = {
  xs: { tagSize: "sm", fontSize: "10px", avatarBoxSize: "14px", avatarFontSize: "8px", gap: 1, px: 1, pl: 0.5, pr: 1.5 },
  sm: { tagSize: "sm", fontSize: "12px", avatarBoxSize: "18px", avatarFontSize: "10px", gap: 1.5, px: 1, pl: 1, pr: 2 },
  md: { tagSize: "md", fontSize: "14px", avatarBoxSize: "22px", avatarFontSize: "11px", gap: 2, px: 2, pl: 1.5, pr: 2.5 },
};

export default function BaseResourceTargetTag({
  icon, name, roleName, size = "sm", remove = undefined, disabled = false, tagLabelProps = {}, ...rest
}: BaseResourceTargetTagProps) {
  const colorStyles = roleStyles(roleName);
  const bgColor = useColorModeValue(colorStyles.light, colorStyles.dark);
  const color = useColorModeValue(colorStyles.dark, colorStyles.light);
  const tokens = SIZE_TOKENS[size];

  const tooltipLabel = roleName ? titleize(roleName) : name;
  const showRemove = !disabled && !!remove;

  const tag = (
    <Tag
      size={tokens.tagSize}
      bg={bgColor}
      pl={tokens.pl}
      pr={showRemove ? tokens.px : tokens.pr}
      py={0}
      {...rest}
    >
      <Avatar
        boxSize={tokens.avatarBoxSize}
        name={icon ? undefined : name}
        icon={icon ? <Icon as={icon} boxSize="60%" /> : undefined}
        bg={color}
        color={bgColor}
        getInitials={(n) => n.charAt(0).toUpperCase()}
        mr={tokens.gap}
        sx={{ "& > div": { fontSize: tokens.avatarFontSize } }}
      />
      <TagLabel
        fontWeight="normal"
        width="max-content"
        color={color}
        fontSize={tokens.fontSize}
        {...tagLabelProps}
      >
        {name}
      </TagLabel>
      {showRemove && (
        <TagCloseButton aria-label="Remove" fontSize="12px" w="8px" color={color} onClick={remove} />
      )}
    </Tag>
  );

  if (roleName) {
    return (
      <Tooltip label={tooltipLabel} placement="top" hasArrow>
        {tag}
      </Tooltip>
    );
  }

  return tag;
}
